import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { UserDisplay } from "@components/common/user-display";
import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseSnapshotProps } from "@custom-types/base-types";
import { Alert, Dialog, Text, useToast } from "@flat-ui";
import { Stack } from "@mui/material";
import {
  SelectedCardItemOption,
  SnapshotMember,
} from "@pages/project-details/project-snapshots/snapshot-member";
import { SnapshotMemberLocationProps } from "@pages/project-details/project-snapshots/snapshot-members";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { removeMemberFromSnapshot } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { SnapshotEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

interface Props extends BaseSnapshotProps, SnapshotMemberLocationProps {
  /** List of members to display */
  members: FaroCoreApiTypes.ProjectMemberBase[];
}

/** Renders the members of a snapshot */
export function SnapshotMemberList({
  members,
  snapshot,
  location,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const { handleErrorWithToast } = useErrorContext();
  const { isRemovingSnapshotMember } = useAppSelector(
    fetchingProjectsFlagsSelector
  );

  const [memberToRemove, setMemberToRemove] =
    useState<FaroCoreApiTypes.ProjectMemberBase | null>(null);

  async function removeMember(): Promise<void> {
    if (!memberToRemove) {
      return;
    }

    trackEvent({
      name: SnapshotEvents.removeMember,
      props: { eventLocation: location },
    });

    try {
      await dispatch(
        removeMemberFromSnapshot({
          companyId,
          member: memberToRemove,
          snapshotId: snapshot.id,
        })
      ).unwrap();

      setMemberToRemove(null);

      openToast({
        title: "Member removed from snapshot",
        message: (
          <RemoveMemberSuccessDescription
            member={memberToRemove}
            subjectType="snapshot"
            entityName={snapshot.name}
          />
        ),
      });
    } catch (error) {
      handleErrorWithToast({
        id: `removeMemberFromSnapshot-${Date.now().toString()}`,
        title: "Could not remove member from snapshot",
        error,
      });
    }
  }

  if (members.length === 0) {
    return (
      <Alert variant="info" title="This snapshot has no invited members" />
    );
  }

  function onSelectOption(
    { target }: SelectedCardItemOption,
    member: FaroCoreApiTypes.ProjectMemberBase
  ): void {
    if (target.value === "remove") {
      setMemberToRemove(member);
    }
  }

  return (
    <>
      <Stack gap={2} sx={{ maxHeight: "300px", overflow: "auto" }}>
        {members.map((member) => (
          <SnapshotMember
            key={member.identity}
            snapshot={snapshot}
            member={member}
            onSelectOption={onSelectOption}
          />
        ))}
      </Stack>

      <Dialog
        title="Remove from snapshot"
        confirmText="Remove"
        open={memberToRemove !== null}
        onConfirm={removeMember}
        onCancel={() => setMemberToRemove(null)}
        showSpinner={isRemovingSnapshotMember}
      >
        <Text
          variant="bodyM"
          sx={{
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
          }}
        >
          When removing member from snapshot they may no longer be able to
          access this snapshot. You can always invite this member again.
        </Text>

        {memberToRemove && <UserDisplay member={memberToRemove} />}
      </Dialog>
    </>
  );
}
